import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  addMonths,
  formatMonthTitle,
  formatWhenLabel,
  getNowCreateSlot,
  labelForCategory,
  startOfMonth,
  timeToMinutes,
  toIsoDateLocal,
} from './constants';
import {
  apiEventToMonthEvent,
  monthEventToApiPayload,
  expandEventToDaySegments,
} from '../planning/eventApiMappers';
import {
  formatApiError,
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
} from '../../services/api';
import AgendaDay from './AgendaDay';
import FilterDatepicker from './FilterDatepicker';
import EventModal from './EventModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';
import './scheduleView.css';

let idCounter = 0;
function nextId() {
  idCounter += 1;
  return `sev-${Date.now()}-${idCounter}`;
}

function isPersistedId(id) {
  return typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id));
}

function firstDayOfMonthIso(monthDate) {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  return toIsoDateLocal(new Date(y, m, 1));
}

function lastDayOfMonthIso(monthDate) {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  return toIsoDateLocal(new Date(y, m + 1, 0));
}

const monthSlideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 64 : -64,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? -64 : 64,
    opacity: 0,
  }),
};

function getApiErrorMessage(err, fallback) {
  const formatted = formatApiError(err);
  return formatted.generalError || err?.message || fallback;
}

const EmployeeScheduleViewPage = forwardRef(function EmployeeScheduleViewPage(
  { onScheduleRangeChange, userId = null, readOnly = false },
  ref
) {
  const { t } = useTranslation('planning');
  const [monthDate, setMonthDate] = useState(() => startOfMonth());
  const [monthDirection, setMonthDirection] = useState(0);
  const monthTitle = useMemo(() => formatMonthTitle(monthDate), [monthDate]);
  const monthKey = monthDate.getTime();

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  const [modal, setModal] = useState(null);
  const [timeError, setTimeError] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { toast, showError, hideToast } = useToast();

  const monthTitleRef = useRef(monthTitle);
  monthTitleRef.current = monthTitle;
  const eventsRef = useRef(events);
  eventsRef.current = events;

  useEffect(() => {
    onScheduleRangeChange?.(monthTitle);
  }, [monthTitle, onScheduleRangeChange]);

  const showEventError = useCallback(
    (err, fallback) => {
      showError(t('schedulingError'), getApiErrorMessage(err, fallback), 5000);
    },
    [showError]
  );

  const reloadMonthEvents = useCallback(
    async (cancelRef = { current: false }) => {
      const dateFrom = filterStart || firstDayOfMonthIso(monthDate);
      const dateTo = filterEnd || lastDayOfMonthIso(monthDate);
      if (!dateFrom || !dateTo || cancelRef?.current) return;

      const filters = { date_from: dateFrom, date_to: dateTo };
      if (userId != null) filters.employe = userId;
      const data = await getEvents(filters);
      const list = Array.isArray(data) ? data : data?.results || [];
      if (cancelRef?.current) return;
      setEvents(list.map(apiEventToMonthEvent));
    },
    [filterEnd, filterStart, monthDate, userId]
  );

  useEffect(() => {
    const cancelRef = { current: false };
    const dateFrom = filterStart || firstDayOfMonthIso(monthDate);
    const dateTo = filterEnd || lastDayOfMonthIso(monthDate);

    (async () => {
      setEventsLoading(true);
      try {
        await reloadMonthEvents(cancelRef);
      } catch (err) {
        if (cancelRef.current) return;
        console.error('Failed to load events', err);
        showEventError(err, t('failedToLoadEvents'));
        setEvents([]);
      } finally {
        if (!cancelRef.current) setEventsLoading(false);
      }
    })();

    return () => {
      cancelRef.current = true;
    };
  }, [filterEnd, filterStart, monthDate, monthKey, reloadMonthEvents, showEventError]);

  const filteredDays = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth();
    const monthPrefix = `${y}-${String(m + 1).padStart(2, '0')}`;

    let list = events.flatMap((ev) =>
      expandEventToDaySegments(ev, {
        useMinutes: false,
        startKey: 'start',
        endKey: 'end',
        dateKey: 'date',
        endDateKey: 'endDate',
      })
    );

    // Keep segments that fall in the visible month (covers spans starting earlier)
    list = list.filter((ev) => String(ev.date || '').startsWith(monthPrefix));

    if (filterStart) {
      list = list.filter((ev) => ev.date >= filterStart);
    }
    if (filterEnd) {
      list = list.filter((ev) => ev.date <= filterEnd);
    }
    if (q) {
      list = list.filter((ev) => {
        const title = String(ev.title || '').toLowerCase();
        const catLabel = labelForCategory(ev.category).toLowerCase();
        const catKey = String(ev.category || '').toLowerCase();
        return title.includes(q) || catLabel.includes(q) || catKey.includes(q);
      });
    }

    list = [...list].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      return timeToMinutes(a.start) - timeToMinutes(b.start);
    });

    const byDate = {};
    list.forEach((ev) => {
      if (!byDate[ev.date]) byDate[ev.date] = [];
      byDate[ev.date].push(ev);
    });

    return Object.keys(byDate)
      .sort()
      .map((date) => ({ date, events: byDate[date] }));
  }, [events, searchQuery, filterStart, filterEnd, monthDate]);

  const closeModal = useCallback(() => {
    setModal(null);
    setTimeError(false);
  }, []);

  const closeDelete = useCallback(() => setDeleteConfirm(null), []);

  const openCreate = useCallback((date, start = '09:00', end = '10:00') => {
    if (readOnly) return;
    setTimeError(false);
    setModal({
      mode: 'create',
      eventId: null,
      title: '',
      date,
      endDate: date,
      start,
      end,
      category: 'HO',
    });
  }, [readOnly]);

  const openEdit = useCallback((event) => {
    if (readOnly) return;
    setTimeError(false);
    const full = eventsRef.current.find((ev) => ev.id === event.id) || event;
    setModal({
      mode: 'edit',
      eventId: full.id,
      title: full.title || '',
      date: full.date,
      endDate: full.endDate || full.date,
      start: full.start,
      end: full.end,
      category: full.category || 'HO',
    });
  }, [readOnly]);

  const openCreateForToday = useCallback((withNowSlot = false) => {
    if (readOnly) return;
    const todayIso = toIsoDateLocal(new Date());
    const slot = withNowSlot ? getNowCreateSlot() : { start: '09:00', end: '10:00' };
    const todayMonth = startOfMonth(new Date());
    const delta = todayMonth.getTime() - monthDate.getTime();
    if (delta !== 0) {
      setMonthDirection(delta > 0 ? 1 : -1);
      setMonthDate(todayMonth);
    }
    openCreate(todayIso, slot.start, slot.end);
  }, [openCreate, monthDate, readOnly]);

  const goPrevMonth = useCallback(() => {
    setMonthDirection(-1);
    setMonthDate((d) => addMonths(d, -1));
  }, []);

  const goNextMonth = useCallback(() => {
    setMonthDirection(1);
    setMonthDate((d) => addMonths(d, 1));
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      goPrevMonth,
      goNextMonth,
      openCreateEvent: () => openCreateForToday(false),
      setSearchQuery,
      getScheduleRangeLabel: () => monthTitleRef.current,
    }),
    [goPrevMonth, goNextMonth, openCreateForToday]
  );

  const handleModalChange = useCallback((patch) => {
    setModal((prev) => (prev ? { ...prev, ...patch } : prev));
    if (patch.start != null || patch.end != null) setTimeError(false);
  }, []);

  const handleModalSubmit = useCallback(async () => {
    if (!modal) return;
    if (timeToMinutes(modal.end) <= timeToMinutes(modal.start)) {
      setTimeError(true);
      return;
    }
    const payload = {
      title: String(modal.title || '').trim(),
      date: modal.date,
      endDate: modal.endDate || modal.date,
      start: modal.start,
      end: modal.end,
      category: modal.category || 'HO',
    };
    if (modal.mode === 'edit' && modal.eventId) {
      const prev = eventsRef.current.find((ev) => ev.id === modal.eventId);
      setEvents((list) =>
        list.map((ev) => (ev.id === modal.eventId ? { ...ev, ...payload } : ev))
      );
      closeModal();
      if (!isPersistedId(modal.eventId)) return;
      try {
        const updated = await updateEvent(modal.eventId, monthEventToApiPayload(payload));
        setEvents((list) =>
          list.map((ev) => (ev.id === modal.eventId ? apiEventToMonthEvent(updated) : ev))
        );
      } catch (err) {
        console.error('Failed to update event', err);
        if (prev) {
          setEvents((list) => list.map((ev) => (ev.id === modal.eventId ? prev : ev)));
        }
        showEventError(err, t('failedToUpdateEvent'));
      }
    } else {
      const tempId = nextId();
      setEvents((list) => [...list, { id: tempId, ...payload }]);
      closeModal();
      try {
        const created = await createEvent(monthEventToApiPayload(payload));
        setEvents((list) =>
          list.map((ev) => (ev.id === tempId ? apiEventToMonthEvent(created) : ev))
        );
      } catch (err) {
        console.error('Failed to create event', err);
        setEvents((list) => list.filter((ev) => ev.id !== tempId));
        showEventError(err, t('failedToCreateEvent'));
      }
    }
  }, [modal, closeModal, showEventError]);

  const requestDelete = useCallback((event, clientX, clientY) => {
    if (readOnly) return;
    const full = eventsRef.current.find((ev) => ev.id === event.id) || event;
    setDeleteConfirm({
      event: full,
      cursorX: clientX,
      cursorY: clientY,
    });
  }, [readOnly]);

  const confirmDelete = useCallback(async () => {
    if (readOnly) return;
    if (!deleteConfirm?.event) return;
    const id = deleteConfirm.event.id;
    const removed =
      eventsRef.current.find((ev) => ev.id === id) || deleteConfirm.event;
    setEvents((prev) => prev.filter((ev) => ev.id !== id));
    setDeleteConfirm(null);
    if (modal?.eventId === id) closeModal();
    if (!isPersistedId(id)) return;
    try {
      await deleteEvent(id);
    } catch (err) {
      console.error('Failed to delete event', err);
      setEvents((prev) => [...prev, removed]);
      showEventError(err, t('failedToDeleteEvent'));
    }
  }, [deleteConfirm, modal, closeModal, readOnly, showEventError]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (deleteConfirm) {
          closeDelete();
          return;
        }
        if (modal) {
          closeModal();
        }
        return;
      }

      if (readOnly) return;

      const isTypingTarget = e.target.closest?.(
        'input, textarea, select, [contenteditable="true"]'
      );
      if (
        e.shiftKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        (e.key === 'N' || e.key === 'n') &&
        !isTypingTarget
      ) {
        e.preventDefault();
        if (deleteConfirm) closeDelete();
        if (!modal) openCreateForToday(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [modal, deleteConfirm, closeModal, closeDelete, openCreateForToday, readOnly]);

  const deleteTitle = deleteConfirm?.event
    ? String(deleteConfirm.event.title || '').trim() ||
      labelForCategory(deleteConfirm.event.category)
    : '';
  const deleteWhen = deleteConfirm?.event
    ? (() => {
        const ev = deleteConfirm.event;
        const endDate = ev.endDate || ev.date;
        if (endDate && endDate !== ev.date) {
          return `${ev.date} ${ev.start} – ${endDate} ${ev.end}`;
        }
        return formatWhenLabel(ev.date, ev.start, ev.end);
      })()
    : '';

  return (
    <div className={`schedule-view-root relative${readOnly ? ' is-readonly' : ''}`}>
      {eventsLoading && (
        <div className="absolute inset-x-0 top-0 z-20 px-4 py-2 text-xs text-text-secondary bg-surface/90 border-b border-border">
          Loading events…
        </div>
      )}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />
      <section className="flex flex-wrap items-center gap-4 px-6 sm:px-8 py-4 border-b border-border bg-surface shrink-0">
        <FilterDatepicker
          startDate={filterStart}
          endDate={filterEnd}
          onStartChange={setFilterStart}
          onEndChange={setFilterEnd}
        />
      </section>

      <section className="relative flex-1 overflow-hidden min-h-0">
        <AnimatePresence initial={false} mode="wait" custom={monthDirection}>
          <motion.div
            key={monthKey}
            custom={monthDirection}
            variants={monthSlideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0 overflow-y-auto px-6 sm:px-8 py-6 scrollbar-hide"
          >
            <div className="max-w-4xl mx-auto space-y-8">
              {filteredDays.length === 0 ? (
                <div className="py-16 text-center">
                  <p className="text-sm font-medium text-text-secondary">{t('noEventsInRange')}</p>
                  {!readOnly && (
                    <button
                      type="button"
                      className="mt-3 text-xs font-medium text-primary hover:underline"
                      onClick={() => openCreateForToday(false)}
                    >
                      + Create Event
                    </button>
                  )}
                </div>
              ) : (
                filteredDays.map((day) => (
                  <AgendaDay
                    key={day.date}
                    date={day.date}
                    events={day.events}
                    onEdit={openEdit}
                    onDelete={requestDelete}
                  />
                ))
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      {!readOnly && (
        <>
          <EventModal
            open={Boolean(modal)}
            mode={modal?.mode || 'create'}
            title={modal?.title || ''}
            date={modal?.date || ''}
            start={modal?.start || '09:00'}
            end={modal?.end || '10:00'}
            category={modal?.category || 'HO'}
            timeError={timeError}
            onChange={handleModalChange}
            onClose={closeModal}
            onSubmit={handleModalSubmit}
          />

          <DeleteConfirmModal
            open={Boolean(deleteConfirm)}
            cursorX={deleteConfirm?.cursorX || 0}
            cursorY={deleteConfirm?.cursorY || 0}
            title={deleteTitle}
            whenLabel={deleteWhen}
            onCancel={closeDelete}
            onConfirm={confirmDelete}
          />
        </>
      )}
    </div>
  );
});

export default EmployeeScheduleViewPage;
